import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, MapPin, CalendarDays, BedDouble, Copy, ArrowRight } from "lucide-react";
import { API_BASE, api } from "../lib/api";
import { usePageMeta } from "../lib/seo";

const TemplateCard = ({ t }) => (
  <Link to={`/routes/${t.slug}`} data-testid={`route-template-card-${t.slug}`}
    className="group bg-card rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 flex flex-col">
    <div className="h-36 bg-[#1B263B] relative overflow-hidden">
      {t.cover_photo_id ? (
        <img src={`${API_BASE}/routes/${t.slug}/cover`} alt={t.name} loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <MapPin className="w-10 h-10 text-[#74C69D]/40" />
        </div>
      )}
      {t.copies > 0 && (
        <span className="absolute top-3 right-3 rounded-full bg-black/60 text-white text-[10px] font-bold px-2.5 py-1 flex items-center gap-1">
          <Copy className="w-3 h-3" /> {t.copies}× gekopieerd
        </span>
      )}
    </div>
    <div className="p-5 flex-1 flex flex-col">
      <h3 className="font-heading font-bold text-lg leading-tight">{t.name}</h3>
      <p className="text-sm text-muted-foreground mt-1">{t.origin_name} → {t.destination_name}{t.round_trip ? " → thuis" : ""}</p>
      <div className="flex flex-wrap gap-2 mt-3 text-[11px] font-semibold">
        <span className="rounded-full bg-secondary px-2.5 py-1 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {t.duration_days} dagen</span>
        <span className="rounded-full bg-secondary px-2.5 py-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.distance_km} km</span>
        {t.vehicle_label && <span className="rounded-full bg-secondary px-2.5 py-1 flex items-center gap-1"><Zap className="w-3 h-3" /> {t.vehicle_label}</span>}
      </div>
      <p className="text-xs text-muted-foreground mt-auto pt-4 flex items-center justify-between">
        <span>Route van {t.owner_name}</span>
        <span className="text-primary font-bold flex items-center gap-1 group-hover:gap-2 transition-all duration-200">Bekijk <ArrowRight className="w-3.5 h-3.5" /></span>
      </p>
    </div>
  </Link>
);

export default function RouteLibrary() {
  usePageMeta("Route-bibliotheek — kant-en-klare EV-roadtrips | VoltRoute",
    "Ontdek EV-roadtrip routes van andere reizigers, inclusief overnachtingen en laadstops. Kopieer een route met één klik naar je eigen auto en reisdata.");
  const [templates, setTemplates] = useState(null);
  useEffect(() => {
    api.get("/routes").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
  }, []);
  return (
    <div className="min-h-screen bg-background" data-testid="route-library-page">
      <header className="bg-[#1B263B] relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="relative max-w-5xl mx-auto px-5 py-14">
          <Link to="/" className="flex items-center gap-2 mb-8 w-fit">
            <span className="w-8 h-8 rounded-full bg-primary flex items-center justify-center"><Zap className="w-4 h-4 text-primary-foreground" /></span>
            <span className="font-heading font-extrabold text-white tracking-tight">VoltRoute</span>
          </Link>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#74C69D] mb-3">Route-bibliotheek</p>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight max-w-2xl leading-tight">
            Kant-en-klare EV-roadtrips van échte reizigers
          </h1>
          <p className="text-white/70 mt-4 max-w-xl text-sm sm:text-base">
            Inclusief overnachtingen, laadstops en dagindeling. Kopieer een route met één klik en VoltRoute rekent hem om naar jouw auto en reisdata.
          </p>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-5 py-12">
        {templates === null ? (
          <div className="flex justify-center py-20"><div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" /></div>
        ) : templates.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="route-library-empty">
            <p className="font-semibold">Nog geen gepubliceerde routes</p>
            <p className="text-sm mt-1">Deel jouw trip via het Delen-venster → "Publiceer als route-template".</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="route-library-grid">
            {templates.map((t) => <TemplateCard key={t.slug} t={t} />)}
          </div>
        )}
      </main>
    </div>
  );
}
